import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, RefreshCw, Save, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function Field({ label, value, onChange, placeholder, textarea }) {
  return (
    <div>
      <label className="text-[11px] text-[var(--orbis-t4)] block mb-1">{label}</label>
      {textarea ? (
        <textarea
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          className="w-full bg-[var(--orbis-s1)] border border-[rgba(255,255,255,0.12)] rounded-md px-2.5 py-1.5 text-xs text-[var(--orbis-t1)] placeholder:text-[var(--orbis-t4)] focus:outline-none resize-none"
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-[var(--orbis-s1)] border border-[rgba(255,255,255,0.12)] rounded-md px-2.5 py-1.5 text-xs text-[var(--orbis-t1)] placeholder:text-[var(--orbis-t4)] focus:outline-none"
        />
      )}
    </div>
  );
}

export default function SeoPanel({ projectId, currentPageSlug, onEdit }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [ogTitle, setOgTitle] = useState('');
  const [ogDescription, setOgDescription] = useState('');
  const [ogImage, setOgImage] = useState('');
  const [canonical, setCanonical] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const authHeader = { Authorization: `Bearer ${localStorage.getItem('orbis_token')}` };
  const pageSlug = currentPageSlug || 'home';

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/magic/projects/${projectId}/meta-read`, {
        params: { page_slug: pageSlug },
        headers: authHeader,
      });
      setTitle(res.data.title || '');
      setDescription(res.data.description || '');
      setOgTitle(res.data.og_title || '');
      setOgDescription(res.data.og_description || '');
      setOgImage(res.data.og_image || '');
      setCanonical(res.data.canonical || '');
    } catch {
      toast.error('Failed to load meta tags');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) load();
  }, [projectId, pageSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/magic/projects/${projectId}/meta-edit`, {
        page_slug: pageSlug,
        title,
        description,
        og_title: ogTitle,
        og_description: ogDescription,
        og_image: ogImage,
        canonical,
      }, { headers: authHeader });
      if (onEdit && res.data?.magic_state) onEdit(res.data.magic_state);
      toast.success('SEO meta tags saved');
    } catch {
      toast.error('Failed to save meta tags');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-[var(--orbis-t3)]" />
          <span className="text-xs text-[var(--orbis-t2)] font-medium">SEO / Meta</span>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="p-1 text-[var(--orbis-t4)] hover:text-[var(--orbis-t1)] transition-colors"
          title="Reload from page"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-4 h-4 animate-spin text-[var(--orbis-t4)]" />
        </div>
      ) : (
        <div className="space-y-2.5">
          <Field label="Page title" value={title} onChange={setTitle} placeholder="My Awesome Product" />
          <Field label="Meta description" value={description} onChange={setDescription}
            placeholder="A short description (150-160 chars)..." textarea />

          <div className="pt-1 border-t border-[rgba(255,255,255,0.07)]">
            <p className="text-[10px] text-[var(--orbis-t4)] mb-2 uppercase tracking-wide">Open Graph</p>
            <div className="space-y-2">
              <Field label="OG Title" value={ogTitle} onChange={setOgTitle} placeholder="Same as page title" />
              <Field label="OG Description" value={ogDescription} onChange={setOgDescription}
                placeholder="Social share description..." textarea />
              <Field label="OG Image URL" value={ogImage} onChange={setOgImage}
                placeholder="https://..." />
            </div>
          </div>

          <div className="pt-1 border-t border-[rgba(255,255,255,0.07)]">
            <Field label="Canonical URL" value={canonical} onChange={setCanonical}
              placeholder="https://yoursite.com/page" />
          </div>

          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-[var(--orbis-amber)] hover:bg-[var(--orbis-amber)]/80 text-[var(--orbis-bg)] text-xs"
          >
            {saving ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Saving...</>
            ) : (
              <><Save className="w-3.5 h-3.5 mr-1.5" /> Save meta tags</>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
